package com.trevorwiebe.timesheet.mileage.presentation.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.FlowRowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.presentation.common.NativeDatePicker
import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import com.trevorwiebe.timesheet.mileage.domain.model.EquipmentType
import com.trevorwiebe.timesheet.mileage.domain.model.TripType
import com.trevorwiebe.timesheet.mileage.domain.model.displayLabel
import com.trevorwiebe.timesheet.mileage.presentation.MileageFilter
import kotlinx.datetime.LocalDate

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun MileageFilterBar(
    filter: MileageFilter,
    equipment: List<Equipment>,
    users: List<DatabaseUserModel>,
    isElevated: Boolean,
    showTripTypeFilter: Boolean = true,
    onEmployeeSelected: (String?) -> Unit,
    onEquipmentSelected: (String?) -> Unit,
    onTripTypesChanged: (Set<TripType>) -> Unit,
    onStartDateClick: () -> Unit,
    onEndDateClick: () -> Unit,
    onStartDateSelected: (LocalDate) -> Unit,
    onEndDateSelected: (LocalDate) -> Unit,
    onDismissStartPicker: () -> Unit,
    onDismissEndPicker: () -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(ChipRowGap)) {
        FlowRow(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 0.dp),
            horizontalArrangement = Arrangement.spacedBy(ChipGap),
            verticalArrangement = Arrangement.spacedBy(ChipRowGap)
        ) {
            SectionLabel("Filter")
            if (isElevated) {
                EmployeeFilterChip(
                    users = users,
                    selectedEmployeeId = filter.employeeId,
                    onSelect = onEmployeeSelected
                )
            }
            EquipmentFilterChip(
                equipment = equipment.filter { it.active && it.type == EquipmentType.TRUCK },
                selectedEquipmentId = filter.equipmentId,
                onSelect = onEquipmentSelected
            )
        }

        FlowRow(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 0.dp),
            horizontalArrangement = Arrangement.spacedBy(ChipGap),
            verticalArrangement = Arrangement.spacedBy(ChipRowGap)
        ) {
            SectionLabel("Date")
            DateChip(
                label = "Start: ${Util.toFriendlyDate(filter.startDate)}",
                onClick = onStartDateClick
            )
            DateChip(
                label = "End: ${Util.toFriendlyDate(filter.endDate)}",
                onClick = onEndDateClick
            )
        }

        if (showTripTypeFilter) {
            FlowRow(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 12.dp, vertical = 0.dp),
                horizontalArrangement = Arrangement.spacedBy(ChipGap),
                verticalArrangement = Arrangement.spacedBy(ChipRowGap)
            ) {
                SectionLabel("Trip Type")
                TripType.entries.forEach { type ->
                    val selected = type in filter.tripTypes
                    FilterChip(
                        selected = selected,
                        onClick = {
                            val updated = if (selected) filter.tripTypes - type else filter.tripTypes + type
                            onTripTypesChanged(updated)
                        },
                        label = { Text(type.displayLabel()) },
                        border = FilterChipDefaults.filterChipBorder(
                            enabled = true,
                            borderColor = MaterialTheme.colorScheme.tertiary,
                            selected = selected
                        ),
                        colors = FilterChipDefaults.filterChipColors(
                            containerColor = MaterialTheme.colorScheme.background,
                            selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                            selectedLabelColor = MaterialTheme.colorScheme.onTertiary
                        )
                    )
                }
            }
        }
    }

    filter.startDate?.let { start ->
        NativeDatePicker(
            date = start,
            showDatePicker = filter.showStartPicker,
            onDismiss = onDismissStartPicker,
            onDateSelected = onStartDateSelected
        )
    }
    filter.endDate?.let { end ->
        NativeDatePicker(
            date = end,
            showDatePicker = filter.showEndPicker,
            onDismiss = onDismissEndPicker,
            onDateSelected = onEndDateSelected
        )
    }
}

private val ChipGap = 6.dp
private val ChipRowGap = (-10).dp

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun FlowRowScope.SectionLabel(text: String) {
    Text(
        text = text,
        fontSize = 14.sp,
        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
        modifier = Modifier.align(Alignment.CenterVertically).padding(end = 4.dp)
    )
}

@Composable
private fun DateChip(label: String, onClick: () -> Unit) {
    FilterChip(
        selected = true,
        onClick = onClick,
        label = { Text(label) },
        border = FilterChipDefaults.filterChipBorder(
            enabled = true,
            borderColor = MaterialTheme.colorScheme.tertiary,
            selected = true
        ),
        colors = FilterChipDefaults.filterChipColors(
            containerColor = MaterialTheme.colorScheme.background,
            selectedContainerColor = MaterialTheme.colorScheme.tertiary,
            selectedLabelColor = MaterialTheme.colorScheme.onTertiary
        )
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun EquipmentFilterChip(
    equipment: List<Equipment>,
    selectedEquipmentId: String?,
    onSelect: (String?) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val selectedLabel = equipment.firstOrNull { it.id == selectedEquipmentId }?.unitNumber
    ExposedDropdownMenuBox(expanded = expanded, onExpandedChange = { expanded = it }) {
        FilterChip(
            selected = selectedEquipmentId != null,
            onClick = { expanded = true },
            label = { Text(if (selectedLabel != null) "Truck: $selectedLabel" else "All Trucks") },
            modifier = Modifier.exposedDropdownSize(false),
            border = FilterChipDefaults.filterChipBorder(
                enabled = true,
                borderColor = MaterialTheme.colorScheme.tertiary,
                selected = selectedEquipmentId != null
            ),
            colors = FilterChipDefaults.filterChipColors(
                containerColor = MaterialTheme.colorScheme.background,
                selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                selectedLabelColor = MaterialTheme.colorScheme.onTertiary
            )
        )
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            containerColor = MaterialTheme.colorScheme.background
        ) {
            if (selectedEquipmentId != null) {
                DropdownMenuItem(
                    text = { Text("All Trucks") },
                    onClick = { onSelect(null); expanded = false }
                )
            }
            equipment.forEach { item ->
                DropdownMenuItem(
                    text = { Text(item.unitNumber) },
                    onClick = { onSelect(item.id); expanded = false }
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun EmployeeFilterChip(
    users: List<DatabaseUserModel>,
    selectedEmployeeId: String?,
    onSelect: (String?) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val selectedLabel = users.firstOrNull { it.id == selectedEmployeeId }?.displayName
    ExposedDropdownMenuBox(expanded = expanded, onExpandedChange = { expanded = it }) {
        FilterChip(
            selected = selectedEmployeeId != null,
            onClick = { expanded = true },
            label = { Text(if (selectedLabel != null) "Employee: $selectedLabel" else "All Employees") },
            modifier = Modifier.exposedDropdownSize(false),
            border = FilterChipDefaults.filterChipBorder(
                enabled = true,
                borderColor = MaterialTheme.colorScheme.tertiary,
                selected = selectedEmployeeId != null
            ),
            colors = FilterChipDefaults.filterChipColors(
                containerColor = MaterialTheme.colorScheme.background,
                selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                selectedLabelColor = MaterialTheme.colorScheme.onTertiary
            )
        )
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            containerColor = MaterialTheme.colorScheme.background,
            modifier = Modifier.widthIn(min = 150.dp)
        ) {
            if (selectedEmployeeId != null) {
                DropdownMenuItem(
                    text = { Text("All Employees") },
                    onClick = { onSelect(null); expanded = false }
                )
            }
            users.forEach { user ->
                DropdownMenuItem(
                    text = { Text(user.displayName) },
                    onClick = { onSelect(user.id); expanded = false }
                )
            }
        }
    }
}
